/**
 * Work Offers Service - API calls for work offers management
 */

import { parseJsonResponse } from '../utils/parseJsonResponse';
import { getFileUrl } from './filesService';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL}/work-offers`;

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que l'offre visée appartient bien à
 * l'appelant. Il lui faut donc une identité prouvée : le JWT signé au login,
 * et non le `userId` que le corps de la requête déclarait jusqu'ici — ce
 * dernier venait du client, et se remplaçait.
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Offer types with colors (moins de types que les demandes)
 */
export const OFFER_TYPES = {
    job_fulltime: { color: '#3b82f6' },       // blue
    job_parttime: { color: '#60a5fa' },       // blue-light
    job_hours: { color: '#06b6d4' },          // cyan
    home_service: { color: '#22c55e' },       // green
    project: { color: '#8b5cf6' },            // violet
};

/**
 * Urgency badges with colors and sort order
 */
export const URGENCY_BADGES = {
    urgent: { color: '#ef4444', order: 1 },      // red
    this_week: { color: '#f97316', order: 2 },   // orange
    asap: { color: '#8b5cf6', order: 3 },        // violet
    this_month: { color: '#eab308', order: 4 },  // yellow
    three_months: { color: '#84cc16', order: 5 }, // lime
    six_months: { color: '#06b6d4', order: 6 },  // cyan
    this_year: { color: '#6b7280', order: 7 },   // gray
};

/**
 * Compensation types with colors
 */
export const COMPENSATION_TYPES = {
    paid: { color: '#22c55e' },        // green
    exchange: { color: '#f59e0b' },    // amber
    volunteer: { color: '#ec4899' },   // pink
};

/**
 * Duration types with colors
 */
export const DURATION_TYPES = {
    hour: { color: '#60a5fa' },      // blue-light
    days: { color: '#3b82f6' },      // blue
    months: { color: '#8b5cf6' },    // violet
    year: { color: '#6366f1' },      // indigo
    undefined: { color: '#6b7280' }, // gray
};

/**
 * Get all public work offers
 * @param {Object} filters - Filters (type, urgency, search, sortBy, sortDir)
 * @returns {Promise<Array>} List of offers
 */
export const getPublicOffers = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.urgency) params.append('urgency', filters.urgency);
        if (filters.search) params.append('search', filters.search);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDir) params.append('sortDir', filters.sortDir);

        const response = await fetch(`${API_BASE}?${params}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch offers');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const offers = data.data ?? data.DATA ?? [];
        return success ? offers : [];
    } catch (error) {
        console.error('Error fetching public offers:', error);
        return [];
    }
};

/**
 * Get user's own offers
 * @param {number} userId - User ID
 * @returns {Promise<Array>} List of user's offers
 */
export const getMyOffers = async (userId) => {
    try {
        const response = await fetch(`${API_BASE}/my?userId=${userId}`, {
            headers: { 'Accept': 'application/json', ...enTetesEcriture(false) }
        });
        if (!response.ok) throw new Error('Failed to fetch my offers');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const offers = data.data ?? data.DATA ?? [];
        return success ? offers : [];
    } catch (error) {
        console.error('Error fetching my offers:', error);
        return [];
    }
};

/**
 * Get single offer by ID
 * @param {number} offerId - Offer ID
 * @returns {Promise<Object|null>} Offer data or null
 */
export const getOfferById = async (offerId) => {
    try {
        const response = await fetch(`${API_BASE}/${offerId}`);
        if (!response.ok) throw new Error('Failed to fetch offer');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const offer = data.data ?? data.DATA ?? null;
        return success ? offer : null;
    } catch (error) {
        console.error('Error fetching offer:', error);
        return null;
    }
};

/**
 * Create a new work offer
 * @param {Object} offerData - Offer data
 * @returns {Promise<Object>} Result with success status
 */
export const createOffer = async (offerData) => {
    try {
        const response = await fetch(`${API_BASE}`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(offerData)
        });
        const { ok, data, error } = await parseJsonResponse(response);
        if (!ok) {
            return { success: false, message: error };
        }
        return {
            success: data.success ?? data.SUCCESS,
            message: data.message ?? data.MESSAGE,
            offerId: data.offerId ?? data.OFFERID,
            // Plafond du plan : lus par lireRefusQuota()
            denyCode: data.denyCode ?? data.DENYCODE,
            current: data.current ?? data.CURRENT,
            max: data.max ?? data.MAX
        };
    } catch (error) {
        console.error('Error creating offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a work offer
 * @param {number} offerId - Offer ID
 * @param {Object} offerData - Update data
 * @returns {Promise<Object>} Result with success status
 */
export const updateOffer = async (offerId, offerData) => {
    try {
        const response = await fetch(`${API_BASE}/${offerId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(offerData)
        });
        const { ok, data, error } = await parseJsonResponse(response);
        if (!ok) {
            return { success: false, message: error };
        }
        return {
            success: data.success ?? data.SUCCESS,
            message: data.message ?? data.MESSAGE
        };
    } catch (error) {
        console.error('Error updating offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a work offer
 * L'identité vient du JWT côté serveur ; il n'est plus nécessaire de passer
 * le userId ici.
 * @param {number} offerId - Offer ID
 * @returns {Promise<Object>} Result with success status
 */
export const deleteOffer = async (offerId) => {
    try {
        const response = await fetch(`${API_BASE}/${offerId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Close a work offer
 * L'identité vient du JWT côté serveur ; il n'est plus nécessaire de passer
 * le userId ici.
 * @param {number} offerId - Offer ID
 * @returns {Promise<Object>} Result with success status
 */
export const closeOffer = async (offerId) => {
    try {
        const response = await fetch(`${API_BASE}/${offerId}/close`, {
            method: 'POST',
            headers: enTetesEcriture()
        });
        const { ok, data, error } = await parseJsonResponse(response);
        if (!ok) {
            return { success: false, message: error };
        }
        return {
            success: data.success ?? data.SUCCESS,
            message: data.message ?? data.MESSAGE
        };
    } catch (error) {
        console.error('Error closing offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get type color
 */
export const getTypeColor = (type) => OFFER_TYPES[type]?.color || '#6b7280';

/**
 * Get type label (uses i18n t.offers.types)
 */
export const getTypeLabel = (type, t) => t?.offers?.types?.[type] || type;

/**
 * Get urgency color
 */
export const getUrgencyColor = (urgency) => URGENCY_BADGES[urgency]?.color || '#6b7280';

/**
 * Get urgency label (uses i18n t.offers.urgencyLabels)
 */
export const getUrgencyLabel = (urgency, t) => t?.offers?.urgencyLabels?.[urgency] || urgency;

/**
 * Get compensation color
 */
export const getCompensationColor = (compensation) => COMPENSATION_TYPES[compensation]?.color || '#6b7280';

/**
 * Get compensation label (uses i18n t.offers.compensationLabels)
 */
export const getCompensationLabel = (compensation, t) => t?.offers?.compensationLabels?.[compensation] || compensation;

/**
 * Get duration color
 */
export const getDurationColor = (duration) => DURATION_TYPES[duration]?.color || '#6b7280';

/**
 * Get duration label (uses i18n t.offers.durationLabels)
 */
export const getDurationLabel = (duration, t) => t?.offers?.durationLabels?.[duration] || duration;

/**
 * Get creator profile with photo by peopleUuid
 */
export const getCreatorProfile = async (peopleUuid) => {
    try {
        const profileResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/peoples/${peopleUuid}`);
        if (!profileResponse.ok) throw new Error('Failed to fetch profile');
        const profileData = await profileResponse.json();

        let photoUrl = null;
        try {
            const photoResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/files/profile-photo/${peopleUuid}`);
            if (photoResponse.ok) {
                const photoData = await photoResponse.json();
                const photoSuccess = photoData.success ?? photoData.SUCCESS;
                // L'API renvoie un objet unique dans DATA, jamais un tableau `files`.
                // Sans photo, DATA vaut {} : filePath est alors absent.
                const photo = photoData.data ?? photoData.DATA ?? {};
                const filePath = photo.filePath ?? photo.filepath ?? photo.FILEPATH;
                if (photoSuccess && filePath) {
                    photoUrl = getFileUrl(filePath);
                }
            }
        } catch (photoError) {
            console.log('No profile photo found');
        }

        const profileSuccess = profileData.success ?? profileData.SUCCESS;
        const profile = profileData.data ?? profileData.DATA ?? null;

        return {
            success: true,
            profile: profileSuccess ? profile : null,
            photoUrl
        };
    } catch (error) {
        console.error('Error fetching creator profile:', error);
        return { success: false, profile: null, photoUrl: null };
    }
};

/**
 * Get occupations for a work offer
 * @param {number} offerId - Offer ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of occupations
 */
export const getOfferOccupations = async (offerId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/${offerId}/occupations?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch occupations');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const occupations = data.data ?? data.DATA ?? [];
        return success ? occupations : [];
    } catch (error) {
        console.error('Error fetching offer occupations:', error);
        return [];
    }
};

/**
 * Get skills for a work offer
 * @param {number} offerId - Offer ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const getOfferSkills = async (offerId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/${offerId}/skills?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch skills');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const skills = data.data ?? data.DATA ?? [];
        return success ? skills : [];
    } catch (error) {
        console.error('Error fetching offer skills:', error);
        return [];
    }
};
